import { AppUtil } from 'kuaidong-lib'

import {
  Clazz,
  Student,
  IParseTitles,
  ClazzAvailableType,
  VacationHomeworkType,
  IEditClazzNoteForm,
  IStudentMatchesInfo,
  IStudentMatchesCheckInfo,
  IStudentMatchesUpdateInfo,
  ISchoolStudentMatchesCheckInfo,
  ISchoolStudentMatchesUpdateInfo,
  IStudentCodeOverView,
  IQueryForStudentList,
  IPaginationStudentTableItem,
  IAddStudentCodeForm,
  ClazzListForSchoolRollNoItem,
  IStudentAccountOverView,
  IPaginationStudentTableItemForStudentAccount,
  IAddStudentAccountForm,
  IClazzListForStudentAccountTable,
  ISportsTeam,
  IPagination,
  IClazzDupStudent,
  IGradeClazzJoin,
} from 'kuaidong-model'

export class AClazz {
  public static baseURL = ''

  /**
   * 全校所有的有老师的班级 (注意有老师)
   * 但没有考虑已设定的合作校的班级，按理如果设定了合作校，按理只返回设定的年级范围和班级列表
   * @returns 班级列表
   */
  public static async all(): Promise<Array<Clazz>> {
    return AppUtil.httpGET('/clazz/school')
  }

  public static async allImported(): Promise<Clazz[]> {
    return AppUtil.httpGET('/web/school_roll_no/list_class')
  }

  public static async allClassListForSchoolManagement(): Promise<Clazz[]> {
    return AppUtil.httpGET('/web/school_management/class_list')
  }

  // 全校所有的班级, 包括没有老师的
  public static async allNotOnlyOwnedByTeacher(): Promise<Array<Clazz>> {
    return AppUtil.httpGET('/clazz/school', {
      only_owned_by_teachers: false,
    })
  }

  public static async allForAdminOwnForTeacher(isAdmin: boolean) {
    return isAdmin ? AClazz.all() : AClazz.allMine()
  }

  // 获得对应年级的所有班级，会附上班级人数等信息
  // onlyOwned: 班级是否是否有老师
  public static async allByGradeNo(gradeNo: number, options = { onlyOwned: true }): Promise<Array<Clazz>> {
    return AppUtil.httpGET(this.baseURL + '/clazz/grade', {
      grade_no: gradeNo,
      only_owned_by_teachers: options.onlyOwned,
    })
  }

  // 后端有逻辑处理教师是否是管理员
  public static async allMine(params?: {
    add_class_manager_set?: boolean
    typ?: number
    semester_name?: string
  }): Promise<Array<Clazz>> {
    return AppUtil.httpGET('/web/classes', params)
  }

  // 我的执教，班级+运动队
  public static async classesSportMine(params?: { typ?: number }): Promise<Array<Clazz>> {
    return AppUtil.httpGET('/clazz/biz', params)
  }

  public static async allSportsTeams(): Promise<ISportsTeam[]> {
    return AppUtil.httpGET('/web/classes/school_sport_team')
  }

  /**
   * 如果设定了合作校，则返回合作校标准的班级列表,
   * 所以这个应该是最常用的学校班级列表的方法,
   * 如果没有合作校，则返回所有的班级，但是会过滤那些没有任何老师和学生的班级
   */
  public static async allClazzes(): Promise<Clazz[]> {
    return AppUtil.httpGET('/web/school_classes')
  }

  // 添加班级的下拉列表展示
  public static async clazzesOptions(schoolId?: string): Promise<IGradeClazzJoin[]> {
    // 返回所有的班级，但是会过滤那些没有任何老师和学生的班级
    return AppUtil.httpGET(this.baseURL + '/web/school_classes/join', { school_id: schoolId })
  }

  public static async createSportsTeam(form: { note_name: string; img_url: string }): Promise<number> {
    return AppUtil.httpPOST('/web/classes/school_sport_team', { ...form })
  }

  public static async allAvailable(
    type?: ClazzAvailableType | VacationHomeworkType | null,
    id?: string | null
  ): Promise<Array<Clazz>> {
    if (type === ClazzAvailableType.ExamPractice) {
      return AppUtil.httpGET('/async/homework_body_exam/classes', { summer_homework_id: id })
    }

    return AppUtil.httpGET('/async/summer/2021/classes', {
      summer_homework_id: id,
      hw_space_type: type,
    })
  }

  public static async findById(id: number): Promise<Clazz> {
    return AppUtil.httpGET(this.baseURL + '/clazz/mine/detail', { id })
  }

  public static async findByTeacherId(teacherId: string): Promise<Clazz[]> {
    return AppUtil.httpGET('/cel/clazzes/teacher_classes', { teacher_id: teacherId })
  }

  public static async findStudent(clazzId: number, userId: string): Promise<Student> {
    return AppUtil.httpGET('/account/student_info', { class_id: clazzId, user_id: userId })
  }

  public static async removeStudent(clazzId: number, userId: string) {
    return AppUtil.httpDelete(`/clazz/student?clazz_id=${clazzId}&user_id=${userId}`)
  }

  // 班级批量删除学生
  public static async batchRemoveStudent(clazzId: number, userIds: string[]) {
    return AppUtil.httpPOST('/clazz/student/remove_in_batch', {
      clazz_id: clazzId,
      user_ids: userIds,
    })
  }

  public static async updateStudent(clazzId: number, userId: string, student: Student) {
    return AppUtil.httpPOST('/async/student/nickname/study_no', {
      class_id: clazzId,
      user_id: userId,
      student_no: student.student_no,
      note_name: student.note_name,
    })
  }

  public static async fileUploadToken(filename: string, scene = 'class-export-student-list-file') {
    return AppUtil.httpGET('/async/common/file_upload_presign', { scene, filename })
  }

  public static async studentsImport(fileUrl: string, clazzId: number) {
    return AppUtil.httpPOST('/web/students_import', {
      file_url: fileUrl,
      class_id: clazzId,
    })
  }

  // 废弃不用
  public static async studentsAdminImport(fileUrl: string) {
    return AppUtil.httpPOST('/web/school/students_imports', {
      file_url: fileUrl,
    })
  }

  public static async setStudentTestFree(student_import_id: string, test_free: boolean) {
    return AppUtil.httpPUT('/web/school/students_import/test_free', {
      student_import_id,
      test_free,
    })
  }

  public static async fetchStudentMatches(clazzId: number): Promise<IStudentMatchesInfo[]> {
    return AppUtil.httpGET('/web/student_matches', {
      class_id: clazzId,
    })
  }

  public static async updateStudentMatches(updateInfo: IStudentMatchesUpdateInfo) {
    return AppUtil.httpPOST('/web/student_matches', updateInfo)
  }

  public static async studentMatchesCheck(clazzId: number): Promise<IStudentMatchesCheckInfo> {
    return AppUtil.httpGET('/web/student_matches/check', {
      class_id: clazzId,
    })
  }

  public static async schoolStudentMatchesCheck(): Promise<ISchoolStudentMatchesCheckInfo> {
    return AppUtil.httpGET('/web/school/student_matches/check')
  }

  public static async updateSchoolStudentMatches(): Promise<ISchoolStudentMatchesUpdateInfo> {
    return AppUtil.httpPOST('/web/school/student_matches/update')
  }

  public static async updateClazzNoteName(updateInfo: IEditClazzNoteForm) {
    if (updateInfo.note_name === '') {
      updateInfo.note_name = null
    }
    return AppUtil.httpPOST('/clazz/mine/class_info', updateInfo)
  }

  public static async updateStudentMatcher() {
    return AppUtil.httpPOST('/web/school/student_matches/update')
  }

  // 废弃
  public static async exportStudents() {
    return AppUtil.httpGET('/web/students/export')
  }

  public static changeStudentClazz(user_id: string, class_id: number) {
    return AppUtil.httpPOST('/clazz/change', {
      user_id,
      class_id,
    })
  }

  // 班级学生认证
  public static async verifyStudent(id: number, userId: string) {
    return AppUtil.httpPOST('/school/student/verification', {
      class_id: id,
      user_id: userId,
    })
  }

  public static async unVerifyStudent(id: number, userId: string) {
    return AppUtil.httpDelete('/school/student/verification', {
      class_id: id,
      user_id: userId,
    })
  }

  public static async studentSchoolRollNoImport(
    fileUrl: string,
    columnMap?: Record<string, string>
  ): Promise<{
    students_count: number
  }> {
    return AppUtil.httpPOST('/web/school_roll_no/school_roll_no_imports', {
      file_url: fileUrl,
      column_map: columnMap,
    })
  }

  public static async studentSchoolRollNoImportParseTitles(url: string): Promise<IParseTitles> {
    return AppUtil.httpPOST('/web/school_roll_no/parse_titles', { excel_url: url })
  }

  public static async studentSchoolRollSummary(
    grade_no: null | number,
    class_id: null | number | undefined
  ): Promise<IStudentCodeOverView> {
    return AppUtil.httpGET('/web/school_roll_no/student_school_roll_summary', {
      grade_no,
      class_id,
    })
  }

  /** 学生列表 */
  public static async studentList(_query: IQueryForStudentList): Promise<IPaginationStudentTableItem> {
    return AppUtil.httpGET('/web/school_roll_no/student_list', _query)
  }
  // 解绑单个学生的学籍号
  public static async unBundlingStudentCode(school_roll_no: string): Promise<void> {
    return AppUtil.httpPUT('/web/school_roll_no/add_a_student', { school_roll_no })
  }

  //   删除单个学籍号
  public static async deleteStudentCode(school_roll_no: string): Promise<void> {
    return AppUtil.httpDelete('/web/school_roll_no/add_a_student', { school_roll_no })
  }

  //  删除全校名单
  public static async deleteAllStudentCode(): Promise<void> {
    return AppUtil.httpDelete('/web/school_roll_no/school_roll_no_imports')
  }

  //  添加单个学生学籍号
  public static async addStudent(studentInfo: IAddStudentCodeForm) {
    return AppUtil.httpPOST('/web/school_roll_no/add_a_student', studentInfo)
  }

  //  班级列表
  public static async clazzListForStudentSchoolRoll(
    limit: number | null,
    offset: number | null
  ): Promise<IPagination<ClazzListForSchoolRollNoItem>> {
    return AppUtil.httpGET('/web/school_roll_no/class_summary_list', { limit, offset })
  }

  //  导出学籍号名单
  public static async schoolRollNoImports(): Promise<{ file_url: string }> {
    return AppUtil.httpGET('/web/school_roll_no/school_roll_no_imports')
  }

  //  导出班级列表
  public static async SchoolRollNoExportClassExcel(): Promise<{ file_url: string }> {
    return AppUtil.httpGET('/web/school_roll_no/export_class_excel')
  }

  //  删除班级 管理员认证校
  public static async DeleteClassForVerifiedSchool(...clazzIds: number[]): Promise<{ file_url: string }> {
    return AppUtil.httpDelete('/web/school_roll_no/class_summary_list', {
      class_id_list: clazzIds,
    })
  }

  //  删除本班名单，保留班级本身
  public static async deleteClazzStudentList(clazzId: number): Promise<void> {
    return AppUtil.httpDelete('/web/school_roll_no/class_summary_list', {
      class_id_list: [clazzId],
      delete_class_entity: false,
    })
  }

  //   合作账号类型认证校
  //  生成账号汇总
  public static async studentAccountSummary(
    grade_no: null | number,
    class_id: null | number | undefined
  ): Promise<IStudentAccountOverView> {
    return AppUtil.httpGET('/web/user_account/user_account_summary', {
      grade_no,
      class_id,
    })
  }

  //  下载学生账号
  public static async studentAccountImports(include_password = false): Promise<{ file_url: string }> {
    return AppUtil.httpGET('/web/user_account/export_student_excel', { include_password })
  }

  // 下载班级账号
  public static async studentAccountClazzImport(): Promise<{ file_url: string }> {
    return AppUtil.httpGET('/web/user_account/export_class_excel')
  }

  // 学生账号学生列表
  public static async studentListForStudentAccount(
    class_id: number | null = null,
    limit: number,
    offset: number,
    name: string | null | undefined
  ): Promise<IPaginationStudentTableItemForStudentAccount> {
    return AppUtil.httpGET('/web/user_account/user_account_student_list', {
      class_id,
      limit,
      offset,
      name,
    })
  }

  // 合作账号删除
  public static async delStudentAccount(login_name: string): Promise<void> {
    return AppUtil.httpDelete('/web/user_account/user_account_student_list', {
      login_name,
    })
  }

  //  解绑手机号
  public static async unBindStudentAccount(login_name: string): Promise<void> {
    return AppUtil.httpPUT('/web/user_account/user_account_student_list', {
      login_name,
    })
  }

  //  添加单个学生账号
  public static async addStudentAccount(studentAccountForm: IAddStudentAccountForm) {
    return AppUtil.httpPOST('/web/user_account/user_account_student_list', studentAccountForm)
  }

  //  学校班级列表
  public static async clazzListForStudentAccount(
    grade_no: number | '',
    limit: number,
    offset: number
  ): Promise<IClazzListForStudentAccountTable> {
    return AppUtil.httpGET('/web/user_account/user_account_class_list', {
      grade_no,
      limit,
      offset,
    })
  }

  // 判断班级是否有重复学生
  public static async checkClazzHasDupStudents(clazzId: string | number): Promise<boolean> {
    const items = await this.getDupStudents(clazzId)
    return items.length > 0
  }

  public static async getDupStudents(clazzId: string | number): Promise<IClazzDupStudent[]> {
    return await AppUtil.httpGET('/clazz/student/dup', { clazz_id: clazzId })
  }

  // 处理班级内重复学生,改名和移除
  public static async dupStudentProcess(
    clazzId: string | number,
    actions: { type: 'rename' | 'remove'; user_id: string; new_name?: string }[]
  ) {
    return await AppUtil.httpPOST('/clazz/student/dup/actions', {
      class_id: clazzId,
      actions,
    })
  }

  // 获得学校的年级列表,会考虑暑假，如果暑假会考虑新入学的年级
  public static async getSchoolGrades(schoolId?: string): Promise<number[]> {
    const infos = await this.clazzesOptions(schoolId)
    return infos.map((item) => item.grade_no)
  }

  /** 按学籍号上传班超人脸图片 */
  public static postStudentFaceImage(_data: {
    student_code: string
    image_url: string
  }): Promise<{
    image_url: string
    student_name: string
    class_name: string
  }> {
    return AppUtil.httpPOST('/web/school_roll_no/student_face_image', _data)
  }
}
